// src/pages/Statements.jsx
import { useState, useEffect, useRef } from 'react'
import { FileText } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { generateAFS, previewAFS, getAFSJobs, getWorkpaper, sendSignOff, exportXBRL, generateReview, validateAFS,
         getReviewStatus, submitForReview, submitReviewDecision, getAuditLog, downloadAFSJob, downloadAFSJobWord } from '../api/kailux'
import BenchmarkReport from './BenchmarkReport'
import BackButton from '../components/BackButton'
import { isUnmappedAccount, buildFiguresFromAccounts } from '../utils/accountMapping'
import { pointsToBilling } from '../utils/lockMessage'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
// XBRL export is shown for all entity types — every SA entity may need CIPC iXBRL filing.
// This is a genuine competitive differentiator: no other SA AFS tool offers this in one click.

// FIX (Sep 2026): several backend gates (disclosure checklist, low-confidence
// mapping confirmation, unmapped accounts, R4 validation) raise their 422s
// with a structured *object* `detail` — {message, missing_items / checks /
// unmapped_accounts / unconfirmed_accounts} — rather than a plain string, so
// the preparer sees exactly what to go fix. Several catch blocks on this page
// (unlike the sign-off handler below, which already guards with
// `detail?.message`) were doing `setError(err?.response?.data?.detail || ...)`
// directly. When `detail` is an object, React throws "Objects are not valid
// as a React child" the moment `{error}` renders — an uncaught render
// exception with no error boundary reporting, which is what actually
// produced the generic "Something went wrong" crash screen for bank-
// statement AFS previews blocked by the (unavoidable, first-time) disclosure
// checklist gate. This helper normalises either shape into a real string,
// and folds in the structured list fields so the message stays actionable
// instead of just becoming a shorter dead end.
// Messages the backend's review generation gate returns (main.py _review_generation_gate).
const isReviewGateError = (msg) =>
  typeof msg === 'string' && /review file|review engagements aren't available/i.test(msg)

const describeApiError = (err, fallback) => {
  const detail = err?.response?.data?.detail
  if (!detail) return fallback
  if (typeof detail === 'string') return detail
  if (typeof detail !== 'object') return fallback
  const message = detail.message || fallback
  const list = detail.missing_items || detail.unmapped_accounts ||
               detail.unconfirmed_accounts ||
               (Array.isArray(detail.checks)
                 ? detail.checks.filter(c => c.status !== 'pass').map(c => c.detail || c.label)
                 : null)
  if (Array.isArray(list) && list.length) {
    return `${message} (${list.join(', ')})`
  }
  return message
}

// Validation checks that can only be fixed on the Review page get a direct route back to it (a job
// resumed straight to Statements skips Review). §117 adds the tax basis and the deferred tax rate choice.
const REVIEW_ACTION_LABELS = {
  tax_provision: 'Answer on the Review page →',
  tax_basis: 'Confirm the tax basis on the Review page →',
  deferred_tax_rate: 'Choose the deferred tax rate on the Review page →',
}
// Trust checks (Oct 2026) are fixed on the Trust Details page, not Review.
const TRUST_ACTION_LABELS = { trust_details: 'Complete the trust details →' }

export default function Statements() {
  const [loading,         setLoading]         = useState(false)
  const [error,           setError]           = useState('')
  const [afsComplete,     setAfsComplete]     = useState(false)
  const [openRPs,         setOpenRPs]         = useState(0)
  const [rpWarningOk,     setRpWarningOk]     = useState(false)
  const [wpJobId,         setWpJobId]         = useState(null)
  const [completedJobId,  setCompletedJobId]  = useState(null)
  const [auditLog,        setAuditLog]        = useState(null)
  const [auditLogOpen,    setAuditLogOpen]    = useState(false)
  const [expandedEntryId, setExpandedEntryId] = useState(null)

  // PDF preview state
  const [pdfBlob,          setPdfBlob]          = useState(null)
  const [previewUrl,       setPreviewUrl]       = useState('')
  const [previewPages,     setPreviewPages]     = useState([])  // Phase 7 — rasterized preview page images (base64 PNG)
  const [showPreview,      setShowPreview]      = useState(false)
  // Phase 7 — distinguishes the free/watermarked/unlimited preview from the
  // real, paid, downloadable final PDF, since both now share the same modal.
  const [previewIsFinal,   setPreviewIsFinal]   = useState(false)
  const [previewLoading,   setPreviewLoading]   = useState(false)

  // iXBRL export state
  const [xbrlLoading,     setXbrlLoading]     = useState(false)
  const [xbrlDownloaded,  setXbrlDownloaded]  = useState(false)
  const [xbrlError,       setXbrlError]       = useState('')

  // Word (.docx) export state — Phase 8C, July 2026
  const [wordLoading,     setWordLoading]     = useState(false)
  const [wordError,       setWordError]       = useState('')

  // Sign-off state
  const [directorEmail,   setDirectorEmail]   = useState('')
  const [sendingSignOff,  setSendingSignOff]  = useState(false)
  const [signOffSent,     setSignOffSent]     = useState(false)
  const [signOffError,    setSignOffError]    = useState('')

  // Phase 2 — internal review gate (opt-in per firm). null until fetched;
  // reviewInfo.require_afs_review tells us whether this firm even uses this.
  const [reviewInfo,          setReviewInfo]          = useState(null)
  const [submittingReview,    setSubmittingReview]    = useState(false)
  const [decisionComment,     setDecisionComment]     = useState('')
  const [submittingDecision,  setSubmittingDecision]  = useState(false)
  const [reviewActionError,   setReviewActionError]   = useState('')

  // R9 — Benchmark Intelligence: ratios fetched after PDF download
  const [ratios,          setRatios]          = useState(null)

  // R4 — Validation gate: pre-generation balance/sanity checks
  const [validation,        setValidation]        = useState(null)
  const [validating,        setValidating]        = useState(false)
  const [validationOverride, setValidationOverride] = useState(false)

  const navigate  = useNavigate()
  const location  = useLocation()
  const { firm }  = useAuth()
  const previewUrlRef = useRef('')

  useEffect(() => {
    return () => {
      if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrlRef.current)
      }
    }
  }, [])

  const client        = location.state?.client
  const mappings          = location.state?.mappings          || {}
  const accounts          = location.state?.accounts          || []
  const prior_figures     = location.state?.prior_figures     || {}
  const first_year        = location.state?.first_year        || false
  const stateFinancialYear = location.state?.financial_year ||
                             sessionStorage.getItem('kailux:financial_year') ||
                             null
  const prior_not_available   = location.state?.prior_not_available   || false
  const bank_statement_source = location.state?.bank_statement_source || false
  // FIX (Sep 2026 — "Back" wipeout on the bank-statement flow, see
  // BankStatement.jsx for the full writeup): opaque payload this page
  // neither reads nor modifies, only round-trips back to Review.jsx so
  // BankStatement.jsx can eventually restore itself from it.
  const bs_snapshot = location.state?.bs_snapshot || null
  // Phase 1 (Jul 2026): explicit period from the free date-range picker.
  // state_year_end previously only served the bank-statement-only path (its
  // sole existing use before this change) — now also falls back to
  // sessionStorage so the TB path's picker survives if this page is
  // revisited directly, same pattern as financial_year above.
  const state_year_end        = location.state?.year_end ||
                                 sessionStorage.getItem('kailux:year_end') || null
  const state_period_start    = location.state?.period_start ||
                                 sessionStorage.getItem('kailux:period_start_display') || null
  const notes                 = location.state?.notes                 || {}
  const notes_summary     = location.state?.notes_summary     || {}

  const deriveYear = () => {
    if (stateFinancialYear) return String(stateFinancialYear)
    const now = new Date()
    const monthNames = ['January','February','March','April','May','June',
      'July','August','September','October','November','December']
    const yearEndMonth = client?.year_end_month || 'February'
    const yearEndIdx   = monthNames.indexOf(yearEndMonth)
    if (yearEndIdx === -1) return String(now.getFullYear())
    return now.getMonth() >= yearEndIdx
      ? String(now.getFullYear())
      : String(now.getFullYear() - 1)
  }

  // Phase 1 (Jul 2026): the free date-range picker's explicit year_end,
  // falling back to the old client.year_end_month-derived display exactly
  // as before when no explicit period was selected. Used everywhere this
  // page shows the reporting date to the preparer, so a stub/non-standard
  // period doesn't visibly contradict what was actually picked upstream.
  const displayYearEnd = state_year_end ||
    `${client?.year_end_month || 'February'} ${deriveYear()}`
  const periodMonths = (() => {
    if (!state_period_start || !state_year_end) return null
    const start = new Date(state_period_start), end = new Date(state_year_end)
    if (!(end > start)) return null
    return Math.round((end - start) / (1000 * 60 * 60 * 24 * 30.4375))
  })()
  const periodWordCap = (periodMonths !== null && (periodMonths < 11 || periodMonths > 13)) ? 'Period' : 'Year'

  // Shared with TrialBalance/Review/Notes and the backend's
  // aggregate_figures_from_accounts() — see utils/accountMapping.js.
  const isUnmappedAcc = (m, name) => isUnmappedAccount(m, name)
  const unmappedAccounts = accounts.filter(acc => isUnmappedAcc(mappings[acc.code], acc.name))

  // Prefer the figures already computed upstream (TrialBalance/Notes/Review) — never
  // silently discard and rebuild them. Only rebuild here if nothing was handed down
  // (e.g. reached this page directly), and when rebuilding, do it via the shared
  // helper so unmapped accounts (including control/suspense rows) are excluded
  // exactly like everywhere else, not silently.
  const computeFigures = () => {
    if (location.state?.figures && Object.keys(location.state.figures).length > 0) {
      return location.state.figures
    }
    if (accounts.length > 0 && Object.keys(mappings).length > 0) {
      return buildFiguresFromAccounts(accounts, mappings)
    }
    return location.state?.figures || {}
  }

  // Arriving directly from AFS History (or anywhere that already knows the
  // job) — trust that job id immediately rather than re-deriving it, and
  // unlock the same results panel (audit log, review actions, sign-off,
  // download) a freshly-generated AFS gets. Previously completedJobId only
  // ever got set at the end of handleGenerate() in THIS session, so
  // navigating back to a client's Statements page later showed the
  // "not yet generated" view even though a completed AFS existed — the
  // audit log, review status, and sign-off actions were then unreachable
  // for anything not generated in the current tab.
  //
  // BUG FIX (2 Sep 2026, found via live AFS-wizard QA): a bare afsJobId in
  // location.state is NOT proof the AFS was ever generated — every job has
  // an id from the moment it's created as a draft, and the normal
  // Review.jsx -> Statements navigation always includes afsJobId regardless
  // of completion status. This effect used to set afsComplete=true purely
  // on that presence check, which showed "AFS generated successfully" with
  // live-looking Download PDF / Download Word buttons for jobs that had
  // never been generated and had no pdf_path at all — a false-positive
  // success state on a compliance-facing screen, with the Download button
  // wired to handleDownload against a file that doesn't exist. The job's
  // real status is now confirmed via getAFSJobs (same source of truth the
  // effect below already uses) before afsComplete is ever set.
  useEffect(() => {
    const jobId = location.state?.afsJobId
    if (!jobId || !firm?.id) return
    setCompletedJobId(jobId)
    let cancelled = false
    ;(async () => {
      try {
        const { data: jobs } = await getAFSJobs(firm.id)
        const job = (jobs || []).find(j => j.id === jobId)
        if (!cancelled && job?.status === 'complete') setAfsComplete(true)
      } catch { /* non-fatal — falls through to the unconfirmed (default) state */ }
    })()
    return () => { cancelled = true }
  }, [location.state?.afsJobId, firm?.id])

  useEffect(() => {
    if (!firm?.id || !client?.id) return
    const checkRPs = async () => {
      try {
        const { data: jobs } = await getAFSJobs(firm.id)
        const done = (jobs || []).filter(j => j.client_id === client.id && j.status === 'complete')
        if (done.length === 0) return
        const latest = done.sort((a,b) => b.financial_year - a.financial_year)[0]
        // Same fix as above, for the case where we reached this page without
        // an explicit afsJobId in state (e.g. straight from the client list)
        // but a completed AFS already exists for this client.
        setCompletedJobId(prev => prev || latest.id)
        setAfsComplete(prev => prev || true)
        const { data } = await getWorkpaper(latest.id)
        if (data?.job) {
          setWpJobId(data.job.id)
          const openCount = (data.review_points || []).filter(rp => rp.status === 'open').length
          setOpenRPs(openCount)
        }
      } catch { /* non-fatal */ }
    }
    checkRPs()
  }, [firm?.id, client?.id])

  // ── R4: pre-generation validation gate ────────────────────────────────────
  useEffect(() => {
    if (!client?.id) return
    const figures = computeFigures()
    if (Object.keys(figures).length === 0) return
    setValidating(true)
    // 29 Sep 2026 (§87): client_id / financial_year / year_end were never sent
    // here, so the server could neither use THIS engagement's tax rate (it fell
    // back to the generic default) nor look up the preparer's recorded answer to
    // "add a tax provision?" — a "declined" answer is what turns the missing-tax
    // warning into a pass. The server verifies the client belongs to the firm.
    // `notes` (28 Oct 2026): without them the panel could never tell that an AI note is older than the
    // figures beside it (e.g. a tax provision added after the notes were drafted).
    validateAFS({ figures, accounts, mappings, prior_figures, first_year, notes,
                  client_id: client.id, financial_year: deriveYear(), year_end: displayYearEnd })
      .then(({ data }) => setValidation(data))
      .catch(() => setValidation(null))
      .finally(() => setValidating(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id])

  useEffect(() => {
    if (!completedJobId) return
    getAuditLog(completedJobId)
      .then(({ data }) => setAuditLog(data.entries || []))
      .catch(() => setAuditLog(null))
  }, [completedJobId])

  const validationBlocked = validation?.overall === 'fail' && !validationOverride
  const validationWarned  = validation?.overall === 'warning' && !validationOverride
  const validationGateOpen = validationBlocked || validationWarned

  // Phase 2 — internal review gate. Only matters if this firm opted in
  // (reviewInfo.require_afs_review); otherwise this silently does nothing
  // and the rest of the page behaves exactly as it did before.
  const refreshReviewStatus = async (jobId) => {
    if (!jobId) return
    try {
      const { data } = await getReviewStatus(jobId)
      setReviewInfo(data)
    } catch {
      setReviewInfo(null)
    }
  }

  useEffect(() => {
    if (completedJobId) refreshReviewStatus(completedJobId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedJobId])

  const handleSubmitForReview = async () => {
    if (!completedJobId) return
    setSubmittingReview(true); setReviewActionError('')
    try {
      await submitForReview({ job_id: completedJobId })
      await refreshReviewStatus(completedJobId)
    } catch {
      setReviewActionError('Could not submit for review. Please try again.')
    } finally {
      setSubmittingReview(false)
    }
  }

  const handleReviewDecision = async (decision) => {
    if (!completedJobId) return
    setSubmittingDecision(true); setReviewActionError('')
    try {
      await submitReviewDecision({ job_id: completedJobId, decision, comment: decisionComment })
      setDecisionComment('')
      await refreshReviewStatus(completedJobId)
    } catch (err) {
      setReviewActionError(
        describeApiError(err, 'Could not record your review decision. Please try again.')
      )
    } finally {
      setSubmittingDecision(false)
    }
  }

  const signoffBlockedByReview = reviewInfo?.require_afs_review && reviewInfo?.review_status !== 'approved'

  // ── AFS Preview (Phase 7) ───────────────────────────────────────────────
  // Free, watermarked, unlimited. Never touches afs_count/credits, never
  // persists an afs_jobs row, never uploads to storage — see the backend's
  // /api/afs/preview docstring. Can be called as many times as needed.
  const handlePreview = async () => {
    if (!client?.id || !firm?.id) {
      setError('Client or firm not loaded. Go back and try again.')
      return
    }
    if (validationGateOpen) {
      setError(validationBlocked
        ? 'This AFS did not pass validation. Review the issue(s) below before generating.'
        : 'Please review and acknowledge the validation warning(s) below before generating.')
      return
    }
    setPreviewLoading(true); setError('')
    try {
      const figures = computeFigures()
      const financial_year = deriveYear()

      const { data } = await previewAFS({
        client_id:          client.id,
        firm_id:            firm.id,
        figures,
        mappings,
        accounts,
        notes,
        engagement_type:    client.engagement_type || firm.engagement_type || 'compilation',
        firm_name:          firm.name || '',
        financial_year,
        prior_figures,
        first_year,
        prior_not_available,
        force_override:          validationOverride,
        bank_statement_source,
      })

      // Rasterized page images (base64 PNG), never real PDF bytes — see the
      // backend's /api/afs/preview docstring. An earlier version of this
      // used a data: URI PDF in an iframe, which was extractable as a real,
      // clean, text-selectable document via devtools in seconds; that's
      // been replaced with image rendering so there's nothing resembling a
      // real PDF for the browser to ever hold.
      setPreviewPages(data.pages || [])
      setPreviewIsFinal(false)
      setShowPreview(true)

      // Ratios are informational only (no credit impact) — fine to refresh
      // on every preview so the Benchmark report stays in sync while reviewing.
      try {
        const { data: reviewData } = await generateReview({
          client_id:       client.id,
          firm_id:         firm.id,
          figures:         figures,
          prior_figures,
          first_year,
          company:         client.name || '',
          year_end:        state_year_end ||
                           `${client.year_end_day ? client.year_end_day + ' ' : ''}${client.year_end_month || 'February'} ${financial_year}`,
          entity_type:     client.entity_type || 'Pty Ltd',
          engagement_type: client.engagement_type || firm.engagement_type || 'compilation',
        })
        setRatios(reviewData?.ratios || null)
      } catch { /* non-fatal */ }

    } catch(err) {
      setError(describeApiError(err, 'Preview generation failed. Please try again.'))
    } finally {
      setPreviewLoading(false)
    }
  }

  // ── Final AFS Generation ────────────────────────────────────────────────
  // The only path that deducts an AFS credit, persists the job, uploads to
  // storage, and produces a real downloadable (unwatermarked) PDF.
  const handleGenerateFinal = async () => {
    if (!client?.id || !firm?.id) {
      setError('Client or firm not loaded. Go back and try again.')
      return
    }
    if (validationGateOpen) {
      setError(validationBlocked
        ? 'This AFS did not pass validation. Review the issue(s) below before generating.'
        : 'Please review and acknowledge the validation warning(s) below before generating.')
      return
    }
    setLoading(true); setError('')
    try {
      const figures = computeFigures()
      const financial_year = deriveYear()

      const response = await generateAFS({
        client_id:          client.id,
        firm_id:            firm.id,
        figures,
        mappings,
        accounts,
        notes,
        engagement_type:    client.engagement_type || firm.engagement_type || 'compilation',
        firm_name:          firm.name || '',
        financial_year,
        // Phase 1 (Jul 2026): explicit period from the free date-range
        // picker. Previously this call never sent year_end/period_start at
        // all for the TB path, so the backend always fell back to the fixed
        // year_end_month/day default — silently discarding whatever the
        // preparer actually selected on Trial Balance.
        year_end:           state_year_end || undefined,
        period_start:       state_period_start || undefined,
        period_end:         state_year_end || undefined,
        prior_figures,
        first_year,
        prior_not_available,
        force_override:          validationOverride,
        bank_statement_source,
      })

      const blob = new Blob([response.data], { type:'application/pdf' })
      setPdfBlob(blob)
      if (previewUrlRef.current) {
        if (previewUrlRef.current.startsWith('blob:')) URL.revokeObjectURL(previewUrlRef.current)
      }
      const url = URL.createObjectURL(blob)
      previewUrlRef.current = url
      setPreviewUrl(url)
      setPreviewIsFinal(true)
      setPreviewPages([])
      setShowPreview(true)
      setAfsComplete(true)

      try {
        const { data: jobs } = await getAFSJobs(firm.id)
        const latestDone = (jobs || [])
          .filter(j => j.client_id === client.id && j.status === 'complete')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
        if (latestDone) setCompletedJobId(latestDone.id)
      } catch { /* non-fatal */ }

      // R9 — fetch ratios for the Benchmark Intelligence report (non-fatal)
      try {
        const { data: reviewData } = await generateReview({
          client_id:       client.id,
          firm_id:         firm.id,
          figures:         figures,
          prior_figures,
          first_year,
          company:         client.name || '',
          year_end:        state_year_end ||
                           `${client.year_end_day ? client.year_end_day + ' ' : ''}${client.year_end_month || 'February'} ${financial_year}`,
          period_start:    state_period_start || undefined,
          date_of_incorporation: client.date_of_incorporation || undefined,
          entity_type:     client.entity_type || 'Pty Ltd',
          engagement_type: client.engagement_type || firm.engagement_type || 'compilation',
        })
        setRatios(reviewData?.ratios || null)
      } catch { /* non-fatal — benchmark report will still render without ratios */ }

    } catch(err) {
      // Phase A (25 Jul 2026): generateAFS uses responseType:'blob' (needed
      // for the real PDF success path), which means an ERROR response body
      // — e.g. the trial-limit or grace-period-lock 402 detail message —
      // arrives as a Blob too, not parsed JSON. err?.response?.data?.detail
      // was therefore always undefined for every failure from this
      // endpoint, silently falling back to the generic message below even
      // when the backend sent a specific, actionable reason (this predates
      // Phase A — was already true for the old trial-limit message; fixing
      // it here so the new grace-period-lock message actually reaches the
      // user, which was an explicit Phase A requirement).
      let detail = err?.response?.data?.detail
      if (!detail && err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text()
          detail = JSON.parse(text)?.detail
        } catch { /* not JSON — fall through to generic message below */ }
      }
      // detail (whether read straight off the error or parsed out of the
      // blob above) can be the same structured object shape described next
      // to describeApiError — never render it directly, same crash risk as
      // the preview/review-decision paths this mirrors.
      const fallback = 'PDF generation failed. Check Railway logs.'
      setError(
        typeof detail === 'string' ? detail :
        (detail && typeof detail === 'object' ? (detail.message || fallback) : fallback)
      )
    } finally {
      setLoading(false)
    }
  }

  const [resumedDownloading, setResumedDownloading] = useState(false)
  const [resumedDownloadError, setResumedDownloadError] = useState('')

  // Downloads the already-generated blob if we have one in memory (the
  // freshly-generated-this-session case). If we don't — e.g. completedJobId
  // was restored from an earlier session, not from just clicking Generate —
  // there's no blob to reuse, so fetch the stored PDF via the same endpoint
  // AFS History already uses, rather than the button silently doing nothing.
  const handleDownload = async () => {
    if (pdfBlob) {
      const url  = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href     = url
      link.download = `${client?.name || 'AFS'}_AFS_${deriveYear()}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(url), 5000)
      return
    }
    if (!completedJobId) return
    setResumedDownloadError('')
    setResumedDownloading(true)
    try {
      const { data } = await downloadAFSJob(completedJobId)
      const url  = URL.createObjectURL(data)
      const link = document.createElement('a')
      link.href     = url
      link.download = `${client?.name || 'AFS'}_AFS_${deriveYear()}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch {
      setResumedDownloadError('Could not fetch the stored PDF. Try regenerating the AFS.')
    } finally {
      setResumedDownloading(false)
    }
  }

  // Downloads the Word (.docx) export generated alongside the PDF during
  // final generation. Needs completedJobId (set after handleGenerate finds
  // the just-completed job), since — unlike the PDF — it's not held in
  // memory as a blob; it's fetched fresh from storage each time.
  const handleDownloadWord = async () => {
    if (!completedJobId) return
    setWordLoading(true); setWordError('')
    try {
      const response = await downloadAFSJobWord(completedJobId)
      const blob = new Blob([response.data],
        { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
      const url  = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href     = url
      link.download = `${client?.name || 'AFS'}_AFS_${deriveYear()}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch (err) {
      setWordError('Word export isn\'t available for this AFS yet — try regenerating, or use the PDF for now.')
    } finally {
      setWordLoading(false)
    }
  }

  // ── iXBRL Export ─────────────────────────────────────────────────────────
  const handleExportXBRL = async () => {
    if (!client?.id || !firm?.id) return
    setXbrlLoading(true); setXbrlError('')

    let figures = computeFigures()

    try {
      const response = await exportXBRL({
        client_id:       client.id,
        firm_id:         firm.id,
        figures,
        prior_figures,
        financial_year:  deriveYear(),
        engagement_type: client.engagement_type || firm.engagement_type || 'compilation',
        // Phase 1 (Jul 2026): explicit period, same as handleGenerate above —
        // if omitted, the backend falls back to looking up the stored job's
        // period_start, but passing it directly here avoids depending on
        // that write having landed first.
        year_end:        state_year_end || undefined,
        period_start:    state_period_start || undefined,
      })

      const blob = new Blob([response.data], { type: 'text/html' })
      const url  = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href  = url
      link.download = `${client.name}_iXBRL_${deriveYear()}.html`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      setXbrlDownloaded(true)
    } catch(err) {
      setXbrlError('iXBRL export failed. Please try again.')
    } finally {
      setXbrlLoading(false)
    }
  }

  const approvedCount = notes_summary?.approved || Object.values(notes).filter(n => n?.status === 'approved').length
  const totalNotes    = notes_summary?.total    || Object.values(notes).length
  const showRPWarning = openRPs > 0 && !rpWarningOk

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif", color:T.tx }}>

      {/* ── PDF Preview Modal ──────────────────────────────────────────────── */}
      {showPreview && (
        <div style={{ position:'fixed', inset:0, zIndex:9999,
          background:'rgba(0,0,0,0.88)', display:'flex', flexDirection:'column' }}>
          <div style={{ background:'#0F172A', borderBottom:'1px solid #1E293B',
            padding:'12px 20px', display:'flex', alignItems:'center',
            justifyContent:'space-between', gap:'16px', flexShrink:0, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px',
                fontWeight:'800', color:'white' }}>
                Kai<span style={{color:'#F97316'}}>Lux</span>
              </div>
              <div style={{ width:'1px', height:'20px', background:'#1E293B' }}/>
              <div>
                <div style={{ fontSize:'13px', fontWeight:'700', color:'white' }}>{client?.name}</div>
                <div style={{ fontSize:'11px', color:'#64748B' }}>
                  Annual Financial Statements — {periodWordCap} ended {displayYearEnd}
                </div>
              </div>
              {previewIsFinal ? (
                <span style={{ fontSize:'10px', padding:'3px 8px', borderRadius:'5px',
                  background:'rgba(52,211,153,0.15)', color:'#34D399', fontWeight:'700' }}>
                  <Icon name="check" /> Final — ready to download
                </span>
              ) : (
                <span style={{ fontSize:'10px', padding:'3px 8px', borderRadius:'5px',
                  background:'rgba(248,113,113,0.15)', color:'#F87171', fontWeight:'700' }}>
                  <Icon name="eye" /> Preview — not for issue · free & unlimited
                </span>
              )}
            </div>
            <div style={{ display:'flex', gap:'10px', flexShrink:0 }}>
              {previewIsFinal ? (
                <>
                  <button onClick={handleDownload}
                    style={{ padding:'9px 20px', borderRadius:'8px', border:'none',
                      background:'#EE6A2C', color:'#0F1013',
                      fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
                      cursor:'pointer', boxShadow:'0 4px 14px rgba(238,106,44,0.35)' }}>
                    ↓ Download PDF
                  </button>
                  {completedJobId && (
                    <button onClick={handleDownloadWord} disabled={wordLoading}
                      title="Editable working-document version — cash flow statement not yet included, see PDF"
                      style={{ padding:'9px 18px', borderRadius:'8px',
                        border:'1px solid #334155', background:'transparent',
                        color: wordLoading ? '#64748B' : '#CBD5E1',
                        fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
                        cursor: wordLoading ? 'default' : 'pointer' }}>
                      {wordLoading ? 'Preparing…' : '↓ Download Word'}
                    </button>
                  )}
                </>
              ) : (
                <button onClick={() => { setShowPreview(false); handleGenerateFinal() }}
                  disabled={loading}
                  style={{ padding:'9px 20px', borderRadius:'8px', border:'none',
                    background: loading ? '#64748B' : '#EE6A2C',
                    color:'#0F1013', fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow:'0 4px 14px rgba(238,106,44,0.35)' }}>
                  {loading ? 'Generating…' : 'Looks good — Generate Final AFS'}
                </button>
              )}
              <button onClick={() => setShowPreview(false)}
                style={{ padding:'9px 18px', borderRadius:'8px',
                  border:'1px solid #334155', background:'transparent',
                  color:'#94A3B8', fontFamily:"'Outfit',sans-serif",
                  fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>
                <Icon name="x" /> Close
              </button>
            </div>
          </div>
          {!previewIsFinal && (
            <div style={{ background:'rgba(248,113,113,0.08)', borderBottom:'1px solid rgba(248,113,113,0.25)',
              padding:'6px 20px', fontSize:'11px', color:'#F87171', flexShrink:0, textAlign:'center' }}>
              This is a watermarked preview for checking accuracy only — it isn't a real, issuable
              document. Nothing is downloadable from here and no AFS credit has been used. Generate
              the final AFS once you're happy with it.
            </div>
          )}
          {previewIsFinal ? (
            <div style={{ flex:1, overflow:'hidden' }}>
              <iframe src={previewUrl} title="AFS Preview"
                style={{ border:'none', width:'100%', height:'100%' }} />
            </div>
          ) : (
            // Phase 7 — rendered as page images, not an embedded PDF viewer.
            // The browser never receives real PDF bytes during preview, so
            // there's no src/blob an inspector can pull a clean, text-
            // selectable document out of — only these watermarked
            // screenshots-of-the-page, with copy/drag/context-menu disabled.
            <div style={{ flex:1, overflow:'auto', padding:'24px', display:'flex',
              flexDirection:'column', alignItems:'center', gap:'18px' }}
              onContextMenu={(e) => e.preventDefault()}>
              {previewPages.length === 0 && (
                <div style={{ color:'#64748B', fontSize:'13px', marginTop:'40px' }}>
                  Rendering preview…
                </div>
              )}
              {previewPages.map((b64, i) => (
                <img key={i} src={`data:image/png;base64,${b64}`}
                  alt={`AFS preview page ${i + 1}`}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  style={{ maxWidth:'900px', width:'100%', height:'auto',
                    boxShadow:'0 4px 24px rgba(0,0,0,0.5)', borderRadius:'2px',
                    userSelect:'none', WebkitUserSelect:'none', pointerEvents:'none' }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Control bar */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`, padding:'12px 28px',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        position:'sticky', top:0, zIndex:50, flexWrap:'wrap', gap:'10px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <BackButton onClick={() => {
            // FIX (Sep 2026 — bank-statement flow now routes through
            // Review; see BankStatementBalanceSheet.jsx/Review.jsx). There
            // is no Notes stage in that flow, and the old unconditional
            // "Back to Notes" button here forwarded only
            // mappings/accounts/prior_figures/first_year/notes — never
            // `figures` — which for the bank-statement flow (where
            // mappings/accounts are always empty) meant every uploaded
            // bank statement + balance sheet figure was silently lost the
            // moment a preparer went back from here after being blocked by
            // the disclosure-checklist gate. Going back now returns to
            // Review instead, which already forwards `figures` correctly.
            if (bank_statement_source) {
              navigate('/review', {
                state: {
                  client, mappings, accounts,
                  figures: computeFigures(),
                  prior_figures, first_year, prior_not_available,
                  source: 'bank_statement', bank_statement_source: true,
                  financial_year: stateFinancialYear, year_end: state_year_end,
                  bs_snapshot,
                }
              })
            } else {
              navigate('/notes', {
                state:{ client, mappings, accounts, prior_figures, first_year, prior_not_available, notes }
              })
            }
          }} label={bank_statement_source ? 'Back to Review' : 'Back to Notes'}
            accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'17px', fontWeight:'800', color:T.tx }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          {client && (
            <span style={{ fontSize:'12px', color:T.mt, borderLeft:`1px solid ${T.bd}`, paddingLeft:'12px' }}>
              {client.name}
            </span>
          )}
          {!first_year && Object.keys(prior_figures).length > 0 && (
            <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px',
              background:alpha(T.gn, 0.15), color:T.gn, fontWeight:'700' }}>
              <Icon name="check" /> Comparatives included
            </span>
          )}
          {first_year && (
            <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px',
              background:alpha(T.tx, 0.05), color:T.mt, fontWeight:'700' }}>
              First year — no comparatives
            </span>
          )}
          {totalNotes > 0 && (
            <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px',
              background:alpha(T.gn, 0.15), color:T.gn, fontWeight:'700' }}>
              <Icon name="check" /> {approvedCount}/{totalNotes} notes approved
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={handlePreview}
            disabled={previewLoading || showRPWarning || validationGateOpen}
            title={validationGateOpen ? 'Resolve the validation issue(s) below first' : 'Free — no AFS credit used'}
            style={{ padding:'8px 16px', borderRadius:'7px',
              border:`1px solid ${alpha(T.or, 0.5)}`, background:'transparent',
              color:(previewLoading||showRPWarning||validationGateOpen)?T.mt:T.or,
              fontFamily:"'Outfit',sans-serif", fontSize:'12px',
              fontWeight:'700', cursor:(previewLoading||showRPWarning||validationGateOpen)?'not-allowed':'pointer' }}>
            {previewLoading ? 'Loading preview…' : <><Icon name="eye" /> Preview</>}
          </button>
          <button onClick={afsComplete ? handleDownload : handleGenerateFinal}
            disabled={loading || resumedDownloading || showRPWarning || validationGateOpen}
            title={validationGateOpen ? 'Resolve the validation issue(s) below first' : undefined}
            style={{ padding:'8px 18px', borderRadius:'7px', border:'none',
              background:(loading||resumedDownloading||showRPWarning||validationGateOpen)?T.mt:T.prg,
              color:T.onpri, fontFamily:"'Outfit',sans-serif", fontSize:'12px',
              fontWeight:'700', cursor:(loading||resumedDownloading||showRPWarning||validationGateOpen)?'not-allowed':'pointer',
              opacity:(loading||resumedDownloading||showRPWarning||validationGateOpen)?0.7:1 }}>
            {loading ? 'Generating...' : resumedDownloading ? 'Fetching PDF...' : afsComplete ? 'Download PDF →' : 'Generate Final AFS →'}
          </button>
        </div>
      </div>
      {resumedDownloadError && (
        <div style={{ color:T.rd, fontSize:'12px', marginTop:'6px', textAlign:'right' }}>
          {resumedDownloadError}
        </div>
      )}

      {error && (
        <div style={{ background:alpha(T.rd, 0.1), borderBottom:`1px solid ${T.rd}`,
          padding:'10px 28px', fontSize:'13px', color:T.rd, display:'flex',
          alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
          <span><Icon name="alert" /> {error}</span>
          {/* Unpaid-account lock (see is_generation_locked in main.py) surfaces
              here as a plain 402 detail string — no structured error code
              exists yet, so this is matched on wording. Fragile but scoped:
              only adds a shortcut button, never blocks anything on its own. */}
          {/* Review engagements: the gate's messages all concern the review file. */}
          {isReviewGateError(error) && (
            <button onClick={() => navigate('/review-file', { state: location.state })}
              style={{ padding:'6px 14px', borderRadius:'6px', border:`1px solid ${T.rd}`,
                background:'transparent', color:T.rd, fontSize:'12px', fontWeight:'700',
                cursor:'pointer', whiteSpace:'nowrap' }}>
              Open the review file →
            </button>
          )}
          {pointsToBilling(error) && (
            <button onClick={() => navigate('/billing')}
              style={{ padding:'6px 14px', borderRadius:'6px', border:`1px solid ${T.rd}`,
                background:'transparent', color:T.rd, fontSize:'12px', fontWeight:'700',
                cursor:'pointer', whiteSpace:'nowrap' }}>
              Go to Billing →
            </button>
          )}
        </div>
      )}

      {/* Open RP warning */}
      {showRPWarning && (
        <div style={{ background:alpha(T.am, 0.08), borderBottom:`1px solid ${alpha(T.am, 0.3)}`,
          padding:'14px 28px', display:'flex', alignItems:'center',
          justifyContent:'space-between', gap:'16px', flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <span style={{ fontSize:'18px' }}><Icon name="flag" /></span>
            <div>
              <div style={{ fontSize:'13px', fontWeight:'700', color:T.yl, marginBottom:'2px' }}>
                {openRPs} open review point{openRPs !== 1 ? 's' : ''} in this client's workpaper
              </div>
              <div style={{ fontSize:'11px', color:T.mt }}>
                Best practice: resolve all review points before generating the final AFS.
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:'10px', flexShrink:0 }}>
            <button onClick={() => navigate('/workpapers', { state:{ client } })}
              style={{ padding:'7px 16px', borderRadius:'7px', border:`1px solid ${alpha(T.am, 0.5)}`,
                background:'transparent', color:T.yl, fontFamily:"'Outfit',sans-serif",
                fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
              Open Workpapers →
            </button>
            <button onClick={() => setRpWarningOk(true)}
              style={{ padding:'7px 16px', borderRadius:'7px', border:`1px solid ${alpha(T.tx, 0.1)}`,
                background:alpha(T.tx, 0.06), color:T.mt,
                fontFamily:"'Outfit',sans-serif", fontSize:'12px', cursor:'pointer' }}>
              Generate anyway
            </button>
          </div>
        </div>
      )}

      {/* R4 — Pre-generation validation panel */}
      {!afsComplete && validation && validation.overall !== 'pass' && (
        <div style={{
          background: validation.overall === 'fail' ? alpha(T.rd, 0.08) : alpha(T.am, 0.08),
          borderBottom: `1px solid ${validation.overall === 'fail' ? alpha(T.rd, 0.3) : alpha(T.am, 0.3)}`,
          padding:'14px 28px' }}>
          <div style={{ fontSize:'13px', fontWeight:'700',
            color: validation.overall === 'fail' ? T.rd : T.yl, marginBottom:'8px' }}>
            {validation.overall === 'fail' ? <><Icon name="block" /> Validation failed</> : <><Icon name="alert" /> Validation warnings</>}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'10px' }}>
            {validation.checks.filter(c => c.status !== 'pass').map((c, i) => (
              <div key={i} style={{ fontSize:'12px', color:T.lt, display:'flex', gap:'8px' }}>
                <span style={{ color: c.status === 'fail' ? T.rd : T.yl, flexShrink:0 }}>
                  {c.status === 'fail' ? <Icon name="x" /> : <Icon name="alert" />}
                </span>
                <span>{c.label ? `${c.label}: ` : ''}{c.detail || c.description}</span>
                {/* A job resumed straight to this page (AFS History) skips the Review
                    page, where the "add a tax provision?" question lives. Offer the
                    direct route back to it rather than leaving only an override box. */}
                {TRUST_ACTION_LABELS[c.action] && (
                  <button type="button" onClick={() => navigate('/trust-details', { state: { ...location.state, figures: computeFigures(), returnTo: '/statements' } })}
                    style={{ marginLeft:'auto', flexShrink:0, padding:'4px 10px', borderRadius:'6px', border:`1px solid ${alpha(T.am, 0.5)}`,
                      background:'transparent', color:T.yl, fontSize:'12px', cursor:'pointer', whiteSpace:'nowrap' }}>
                    {TRUST_ACTION_LABELS[c.action]}
                  </button>
                )}
                {REVIEW_ACTION_LABELS[c.action] && (
                  <button type="button" onClick={() => navigate('/review', { state: { ...location.state, figures: computeFigures() } })}
                    style={{ marginLeft:'auto', flexShrink:0, padding:'4px 10px', borderRadius:'6px', border:`1px solid ${alpha(T.am, 0.5)}`,
                      background:'transparent', color:T.yl, fontSize:'12px', cursor:'pointer', whiteSpace:'nowrap' }}>
                    {REVIEW_ACTION_LABELS[c.action]}
                  </button>
                )}
              </div>
            ))}
          </div>
          <label style={{ display:'flex', alignItems:'flex-start', gap:'8px', fontSize:'12px',
            color:T.lt, cursor:'pointer', maxWidth:'620px' }}>
            <input type="checkbox" checked={validationOverride}
              onChange={e => setValidationOverride(e.target.checked)}
              style={{ marginTop:'2px' }} />
            <span>
              {validation.overall === 'fail'
                ? 'I have reviewed the failure(s) above, understand the financial statements may not balance, and want to generate anyway. A note will be added to the PDF.'
                : 'I have reviewed this warning and confirm the figures are correct.'}
            </span>
          </label>
        </div>
      )}

      {!afsComplete && validating && (
        <div style={{ padding:'8px 28px', fontSize:'11px', color:T.mt }}>
          Running validation checks…
        </div>
      )}

      <div style={{ padding:'40px 24px' }}>
        <div style={{ maxWidth:'720px', margin:'0 auto', background:T.s2,
          borderRadius:'4px', padding:'48px 56px',
          boxShadow:T.sh, minHeight:'600px',
          fontFamily:"Georgia, 'Times New Roman', serif" }}>

          {/* Letterhead */}
          <div style={{ textAlign:'center', marginBottom:'32px',
            borderBottom:`2px solid ${T.tx}`, paddingBottom:'24px' }}>
            <div style={{ fontSize:'18px', fontWeight:'700', marginBottom:'4px',
              fontFamily:"'Syne',sans-serif" }}>
              {client?.name?.toUpperCase() || 'COMPANY NAME'}
            </div>
            <div style={{ fontSize:'11px', color:T.mt, marginBottom:'2px' }}>
              {client?.reg_number || 'Registration Number: XXXX/XXXXXX/XX'}
            </div>
            <div style={{ fontSize:'11px', color:T.mt, marginBottom:'16px' }}>
              Incorporated in the Republic of South Africa
            </div>
            <div style={{ fontSize:'13px', fontWeight:'700', fontFamily:"'Syne',sans-serif",
              textTransform:'uppercase', letterSpacing:'2px' }}>
              Annual Financial Statements
            </div>
            <div style={{ fontSize:'11px', color:T.mt, marginTop:'4px' }}>
              For the {periodWordCap.toLowerCase()} ended {displayYearEnd}
            </div>
            <div style={{ fontSize:'9px', color:T.mt, marginTop:'8px', fontStyle:'italic' }}>
              Prepared in accordance with IFRS for SMEs · Generated by KaiLux
            </div>
          </div>

          <div style={{ textAlign:'center', padding:'32px 20px', color:T.mt }}>
            {afsComplete ? (
              <>
                <div style={{ fontSize:'52px', marginBottom:'20px' }}><Icon name="checkCircle" strokeWidth={1.5} style={{ color: T.gn }} /></div>
                <div style={{ fontSize:'18px', fontWeight:'700', color:T.tx, marginBottom:'10px' }}>
                  AFS generated successfully
                </div>
                <div style={{ fontSize:'13px', lineHeight:'1.8', marginBottom:'28px' }}>
                  Your Annual Financial Statements are ready. Preview them or download now.
                </div>

                {/* Action buttons */}
                <div style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
                  {pdfBlob && (
                    <button onClick={() => setShowPreview(true)}
                      style={{ padding:'11px 24px', borderRadius:'9px', border:'none',
                        background:T.prg, color:T.onpri,
                        fontFamily:"'Outfit',sans-serif", fontSize:'13px',
                        fontWeight:'700', cursor:'pointer' }}>
                      <Icon name="eye" /> Preview AFS
                    </button>
                  )}
                  <button onClick={handleDownload} disabled={resumedDownloading}
                    style={{ padding:'11px 24px', borderRadius:'9px', border:`1px solid ${T.or}`,
                      background:'transparent', color:T.or, fontFamily:"'Outfit',sans-serif",
                      fontSize:'13px', fontWeight:'700',
                      cursor: resumedDownloading ? 'not-allowed' : 'pointer',
                      opacity: resumedDownloading ? 0.6 : 1 }}>
                    {resumedDownloading ? 'Fetching...' : '↓ Download PDF'}
                  </button>
                  {resumedDownloadError && (
                    <div style={{ width:'100%', color:T.rd, fontSize:'12px', textAlign:'center' }}>
                      {resumedDownloadError}
                    </div>
                  )}
                  {completedJobId && (
                    <button onClick={handleDownloadWord} disabled={wordLoading}
                      title="Editable working-document version — cash flow statement not yet included, see PDF"
                      style={{ padding:'11px 24px', borderRadius:'9px', border:`1px solid ${T.mt}`,
                        background:'transparent', color: wordLoading ? T.mt : T.lt,
                        fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
                        cursor: wordLoading ? 'default' : 'pointer' }}>
                      {wordLoading ? 'Preparing…' : '↓ Download Word'}
                    </button>
                  )}
                  <button onClick={() => navigate('/workpapers', { state:{ client } })}
                    style={{ padding:'11px 24px', borderRadius:'9px', border:'none',
                      background:T.pu, color:T.onpri,
                      fontFamily:"'Outfit',sans-serif", fontSize:'13px',
                      fontWeight:'700', cursor:'pointer' }}>
                    Open Workpapers →
                  </button>
                  <button onClick={() => navigate('/clients')}
                    style={{ padding:'11px 24px', borderRadius:'9px', border:'none',
                      background:T.prg, color:T.onpri, fontFamily:"'Outfit',sans-serif",
                      fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>
                    Back to Clients
                  </button>
                </div>
                {wordError && (
                  <div style={{ fontSize:'12px', color:T.rd, marginTop:'10px' }}>
                    {wordError}
                  </div>
                )}

                {/* ── Additional Documents — Enrich for Accuracy ───────────────────── */}
                <div style={{ marginTop:'16px', padding:'20px 24px',
                  background:alpha(T.am, 0.07), border:`1.5px solid ${alpha(T.am, 0.3)}`,
                  borderRadius:'12px', textAlign:'left' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:'14px', flexWrap:'wrap' }}>
                    <div style={{ flex:1, minWidth:'200px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
                        <span style={{ fontSize:'18px' }}><Icon name="attach" /></span>
                        <div style={{ fontSize:'13px', fontWeight:'700', color:T.am }}>
                          Add Supporting Documents to Improve Accuracy
                        </div>
                      </div>
                      <div style={{ fontSize:'12px', color:T.am, lineHeight:'1.7' }}>
                        Upload a fixed asset register, loan agreement, lease, minutes or other source document.
                        KaiLux will extract the figures and enrich your notes automatically — then you can
                        regenerate the PDF with source-verified data.
                      </div>
                      <div style={{ marginTop:'10px', display:'flex', gap:'8px', flexWrap:'wrap' }}>
                        {['Fixed Assets','Loan Agreement','Lease','Minutes'].map(tag => (
                          <span key={tag} style={{ fontSize:'10px', fontWeight:'600', padding:'3px 8px',
                            borderRadius:'5px', background:alpha(T.am, 0.15), color:T.am,
                            border:`1px solid ${alpha(T.am, 0.3)}` }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                    <button
                      disabled={!completedJobId}
                      onClick={() => navigate('/additional-documents', {
                        state:{
                          afsJobId:   completedJobId,
                          clientId:   client?.id,
                          clientName: client?.name,
                          // pass full state so we can return and regenerate
                          returnState: { client, mappings, accounts,
                            prior_figures, first_year, prior_not_available, notes },
                        }
                      })}
                      style={{ alignSelf:'center', padding:'11px 20px', borderRadius:'9px',
                        border:'none', background: completedJobId
                          ? T.am : T.mt,
                        color:T.onpri, fontFamily:"'Outfit',sans-serif", fontSize:'13px',
                        fontWeight:'700', cursor: completedJobId ? 'pointer' : 'not-allowed',
                        whiteSpace:'nowrap' }}>
                      Add Documents →
                    </button>
                  </div>
                </div>

                {/* ── iXBRL Export Card ────────────────────────────────────── */}
                <div style={{ marginTop:'24px', padding:'20px 24px',
                  background:T.bld, border:`1.5px solid ${alpha(T.bl, 0.3)}`,
                  borderRadius:'12px', textAlign:'left' }}>

                  <div style={{ display:'flex', justifyContent:'space-between',
                    alignItems:'flex-start', gap:'16px', flexWrap:'wrap' }}>
                    <div>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px' }}>
                        <span style={{ fontSize:'18px' }}><Icon name="chart" /></span>
                        <div style={{ fontSize:'13px', fontWeight:'700', color:T.bl }}>
                          Export iXBRL for CIPC Submission
                        </div>
                        <span style={{ fontSize:'9px', fontWeight:'700', padding:'2px 7px',
                          borderRadius:'4px', background:T.bl, color:T.onpri,
                          letterSpacing:'0.5px' }}>
                          REGULATORY
                        </span>
                      </div>
                      <div style={{ fontSize:'12px', color:T.bl, lineHeight:'1.6',
                        maxWidth:'380px' }}>
                        Generate a CIPC-compatible iXBRL file — the same financial statements
                        with all monetary values tagged using the{' '}
                        <strong>IFRS Taxonomy 2023</strong>. Open in any browser,
                        submit directly to the CIPC eServices portal.
                      </div>
                      {xbrlError && (
                        <div style={{ marginTop:'8px', fontSize:'12px', color:T.rd }}>
                          <Icon name="alert" /> {xbrlError}
                        </div>
                      )}
                      {xbrlDownloaded && (
                        <div style={{ marginTop:'8px', fontSize:'12px', color:T.gn,
                          fontWeight:'600' }}>
                          <Icon name="check" /> iXBRL file downloaded — open it in your browser or submit to CIPC.
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleExportXBRL}
                      disabled={xbrlLoading}
                      style={{ padding:'11px 20px', borderRadius:'9px', border:'none',
                        background: xbrlDownloaded ? T.gn : T.bl,
                        opacity: xbrlLoading ? 0.6 : 1,
                        color:T.onpri, fontFamily:"'Outfit',sans-serif",
                        fontSize:'12px', fontWeight:'700',
                        cursor: xbrlLoading ? 'not-allowed' : 'pointer',
                        whiteSpace:'nowrap', flexShrink:0,
                        display:'flex', alignItems:'center', gap:'8px',
                        boxShadow: xbrlLoading ? 'none' : `0 4px 14px ${alpha(T.bl, 0.3)}`,
                        transition:'all 0.25s' }}>
                      {xbrlLoading ? (
                        <>
                          <span style={{ width:'12px', height:'12px',
                            border:`2px solid ${alpha(T.onpri, 0.3)}`,
                            borderTopColor:T.onpri, borderRadius:'50%',
                            animation:'spin 0.8s linear infinite', display:'inline-block' }}/>
                          Building iXBRL...
                        </>
                      ) : xbrlDownloaded
                        ? <><Icon name="check" /> Downloaded</>
                        : '↓ Export iXBRL (.html)'}
                    </button>
                  </div>

                  {/* CIPC steps — shown after download */}
                  {xbrlDownloaded && (
                    <div style={{ marginTop:'16px', padding:'12px 16px',
                      background:T.s2, borderRadius:'8px',
                      border:`1px solid ${alpha(T.bl, 0.3)}`, fontSize:'11px',
                      color:T.lt, lineHeight:'1.7' }}>
                      <div style={{ fontWeight:'700', color:T.bl, marginBottom:'8px',
                        fontSize:'10px', letterSpacing:'1px' }}>
                        HOW TO SUBMIT TO CIPC
                      </div>
                      <ol style={{ paddingLeft:'16px', margin:0 }}>
                        <li>Go to <strong>eservices.cipc.co.za</strong> and log in</li>
                        <li>Navigate to <strong>Annual Returns → File Annual Return</strong></li>
                        <li>Upload the downloaded <strong>.html</strong> file in the iXBRL section</li>
                        <li>The CIPC validator reads all embedded XBRL tags automatically</li>
                        <li>Review the validation report, then submit</li>
                      </ol>
                    </div>
                  )}
                </div>

                {/* ── Internal Review Card (Phase 2, opt-in per firm) ─────── */}
                {reviewInfo?.require_afs_review && (
                  <div style={{ marginTop:'20px', padding:'20px 24px',
                    background:T.s3, border:`1px solid ${T.bd}`,
                    borderRadius:'12px', textAlign:'left' }}>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:T.tx,
                      marginBottom:'4px' }}>
                      <Icon name="search" /> Internal Review
                    </div>
                    <div style={{ fontSize:'12px', color:T.mt, lineHeight:'1.6', marginBottom:'14px' }}>
                      This firm requires internal review before an AFS can be sent for
                      director sign-off.
                    </div>

                    {reviewActionError && (
                      <div style={{ padding:'8px 12px', background:alpha(T.rd, 0.08),
                        border:`1px solid ${alpha(T.rd, 0.2)}`, borderRadius:'7px',
                        fontSize:'12px', color:T.rd, marginBottom:'10px' }}>
                        {reviewActionError}
                      </div>
                    )}

                    {(!reviewInfo.review_status || reviewInfo.review_status === 'changes_requested') && (
                      <>
                        {reviewInfo.review_status === 'changes_requested' && (
                          <div style={{ padding:'10px 14px', background:alpha(T.or, 0.08),
                            border:`1px solid ${alpha(T.or, 0.25)}`, borderRadius:'8px',
                            fontSize:'12px', color:T.tx, marginBottom:'10px', lineHeight:'1.6' }}>
                            <strong>Changes requested{reviewInfo.reviewed_by_email ? ` by ${reviewInfo.reviewed_by_email}` : ''}:</strong>{' '}
                            {reviewInfo.review_comment || '(no comment provided)'}
                          </div>
                        )}
                        <button disabled={submittingReview} onClick={handleSubmitForReview}
                          style={{ padding:'9px 16px', borderRadius:'8px', border:'none',
                            background:T.prg, color:T.onpri,
                            fontFamily:"'Outfit',sans-serif", fontSize:'12px', fontWeight:'700',
                            cursor: submittingReview ? 'not-allowed' : 'pointer' }}>
                          {submittingReview ? 'Submitting…' : 'Submit for Review'}
                        </button>
                      </>
                    )}

                    {reviewInfo.review_status === 'pending_review' && (
                      <>
                        <div style={{ padding:'10px 14px', background:alpha(T.mt, 0.08),
                          border:`1px solid ${alpha(T.mt, 0.2)}`, borderRadius:'8px',
                          fontSize:'12px', color:T.tx, marginBottom: reviewInfo.can_review ? '14px' : '0' }}>
                          <Icon name="hourglass" /> Awaiting internal review.
                        </div>
                        {reviewInfo.can_review && (
                          <>
                            <textarea value={decisionComment}
                              onChange={e => setDecisionComment(e.target.value)}
                              placeholder="Optional comment (required if requesting changes)"
                              style={{ width:'100%', minHeight:'60px', padding:'9px 12px',
                                background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'8px',
                                fontSize:'12px', fontFamily:"'Outfit',sans-serif", outline:'none',
                                color:T.tx, marginBottom:'10px', resize:'vertical' }}/>
                            <div style={{ display:'flex', gap:'8px' }}>
                              <button disabled={submittingDecision}
                                onClick={() => handleReviewDecision('approved')}
                                style={{ flex:1, padding:'9px 12px', borderRadius:'8px', border:'none',
                                  background:T.gn, color:T.onpri,
                                  fontFamily:"'Outfit',sans-serif", fontSize:'12px', fontWeight:'700',
                                  cursor: submittingDecision ? 'not-allowed' : 'pointer' }}>
                                Approve
                              </button>
                              <button disabled={submittingDecision}
                                onClick={() => handleReviewDecision('changes_requested')}
                                style={{ flex:1, padding:'9px 12px', borderRadius:'8px',
                                  border:`1px solid ${T.bd}`, background:T.s2, color:T.tx,
                                  fontFamily:"'Outfit',sans-serif", fontSize:'12px', fontWeight:'700',
                                  cursor: submittingDecision ? 'not-allowed' : 'pointer' }}>
                                Request Changes
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {reviewInfo.review_status === 'approved' && (
                      <div style={{ padding:'10px 14px', background:alpha(T.gn, 0.08),
                        border:`1px solid ${alpha(T.gn, 0.25)}`, borderRadius:'8px',
                        fontSize:'12px', color:T.gn, fontWeight:'600' }}>
                        <Icon name="check" /> Approved{reviewInfo.reviewed_by_email ? ` by ${reviewInfo.reviewed_by_email}` : ''}
                        {reviewInfo.review_comment ? ` — "${reviewInfo.review_comment}"` : ''}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Director Sign-off Card ───────────────────────────────── */}
                <div style={{ marginTop:'20px', padding:'20px 24px',
                  background:T.s3, border:`1px solid ${T.bd}`,
                  borderRadius:'12px', textAlign:'left' }}>
                  <div style={{ fontSize:'13px', fontWeight:'700', color:T.tx,
                    marginBottom:'4px' }}>
                    <Icon name="note" /> Send for Director Sign-off
                  </div>
                  <div style={{ fontSize:'12px', color:T.mt, lineHeight:'1.6', marginBottom:'14px' }}>
                    Send a secure link to the director, member, or trustee to electronically
                    confirm responsibility for these financial statements.
                  </div>
                  {signOffSent ? (
                    <div style={{ padding:'10px 14px', background:alpha(T.gn, 0.08),
                      border:`1px solid ${alpha(T.gn, 0.25)}`, borderRadius:'8px',
                      fontSize:'12px', color:T.gn, fontWeight:'600' }}>
                      <Icon name="check" /> Sign-off link sent to {directorEmail}.
                    </div>
                  ) : signoffBlockedByReview ? (
                    <div style={{ padding:'10px 14px', background:alpha(T.mt, 0.08),
                      border:`1px solid ${alpha(T.mt, 0.2)}`, borderRadius:'8px',
                      fontSize:'12px', color:T.tx }}>
                      This AFS must be internally reviewed and approved before it can be sent
                      for director sign-off — see the Internal Review section above.
                    </div>
                  ) : (
                    <>
                      {signOffError && (
                        <div style={{ padding:'8px 12px', background:alpha(T.rd, 0.08),
                          border:`1px solid ${alpha(T.rd, 0.2)}`, borderRadius:'7px',
                          fontSize:'12px', color:T.rd, marginBottom:'10px' }}>
                          {signOffError}
                        </div>
                      )}
                      <div style={{ display:'flex', gap:'8px' }}>
                        <input type="email" value={directorEmail}
                          onChange={e => setDirectorEmail(e.target.value)}
                          placeholder={`${client?.entity_type === 'CC' ? 'Member' : client?.entity_type === 'Trust' ? 'Trustee' : 'Director'} email address`}
                          style={{ flex:1, padding:'9px 12px', background:T.s2,
                            border:`1px solid ${T.bd}`, borderRadius:'8px',
                            fontSize:'12px', fontFamily:"'Outfit',sans-serif",
                            outline:'none', color:T.tx }}
                          onFocus={e => e.target.style.borderColor = T.or}
                          onBlur={e  => e.target.style.borderColor = T.bd}/>
                        <button
                          disabled={sendingSignOff || !directorEmail.includes('@')}
                          onClick={async () => {
                            if (!completedJobId || !directorEmail.includes('@')) return
                            setSendingSignOff(true); setSignOffError('')
                            try {
                              await sendSignOff({
                                job_id:         completedJobId,
                                director_email: directorEmail,
                                director_name:  client?.director_name?.split('\n')[0]?.trim() || '',
                                firm_name:      firm?.name || '',
                                client_name:    client?.name || '',
                                year_end:       displayYearEnd,
                              })
                              setSignOffSent(true)
                            } catch (err) {
                              setSignOffError(
                                err?.response?.data?.detail?.message ||
                                'Could not send sign-off link. Please try again.'
                              )
                            } finally {
                              setSendingSignOff(false)
                            }
                          }}
                          style={{ padding:'9px 18px', borderRadius:'8px', border:'none',
                            background: (sendingSignOff || !directorEmail.includes('@'))
                              ? T.mt
                              : T.prg,
                            color:T.onpri, fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                            fontWeight:'700',
                            cursor: (sendingSignOff || !directorEmail.includes('@'))
                              ? 'not-allowed' : 'pointer',
                            whiteSpace:'nowrap' }}>
                          {sendingSignOff ? 'Sending...' : 'Send Link →'}
                        </button>
                      </div>
                      <div style={{ fontSize:'10px', color:T.mt, marginTop:'6px' }}>
                        The director does not need a KaiLux account. The link expires after 30 days.
                      </div>
                    </>
                  )}
                </div>

                {/* ── Activity Log (audit trail) ───────────────────────────── */}
                {auditLog && auditLog.length > 0 && (
                  <div style={{ marginTop:'20px', padding:'16px 20px',
                    background:T.s3, border:`1px solid ${T.bd}`,
                    borderRadius:'12px', textAlign:'left' }}>
                    <div onClick={() => setAuditLogOpen(o => !o)}
                      style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                        cursor:'pointer' }}>
                      <div style={{ fontSize:'13px', fontWeight:'700', color:T.tx }}>
                        <Icon name="log" /> Activity Log ({auditLog.length})
                      </div>
                      <div style={{ fontSize:'12px', color:T.mt }}>{auditLogOpen ? '▲' : '▼'}</div>
                    </div>
                    {auditLogOpen && (
                      <div style={{ marginTop:'14px', display:'flex', flexDirection:'column', gap:'10px' }}>
                        {auditLog.map(entry => (
                          <div key={entry.id} style={{ fontSize:'12px', lineHeight:'1.6' }}>
                            <div onClick={() => setExpandedEntryId(id => id === entry.id ? null : entry.id)}
                              style={{ cursor: (entry.diff && Object.values(entry.diff).some(d => d && Object.keys(d).length))
                                ? 'pointer' : 'default' }}>
                              <span style={{ color:T.tx, fontWeight:'600' }}>{entry.summary}</span>
                              <span style={{ color:T.mt }}>
                                {' — '}{entry.actor_email || 'unknown'}
                                {', '}{new Date(entry.created_at).toLocaleString()}
                              </span>
                            </div>
                            {expandedEntryId === entry.id && entry.diff && (
                              <pre style={{ marginTop:'6px', padding:'10px 12px', background:T.s2,
                                border:`1px solid ${T.bd}`, borderRadius:'7px', fontSize:'11px',
                                overflow:'auto', color:T.lt }}>
                                {JSON.stringify(entry.diff, null, 2)}
                              </pre>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── R9: Business Intelligence / Benchmark Report ─────────── */}
                <BenchmarkReport
                  client={client}
                  ratios={ratios}
                  industry={client?.industry}
                  yearEnd={displayYearEnd}
                  periodStart={state_period_start}
                />

              </>
            ) : (
              <>
                <div style={{ marginBottom:'20px' }}><FileText size={44} strokeWidth={1.5} color={T.tx}/></div>
                <div style={{ fontSize:'18px', fontWeight:'700', color:T.tx, marginBottom:'10px' }}>
                  Your AFS is ready to generate
                </div>
                <div style={{ fontSize:'13px', lineHeight:'1.8', marginBottom:'8px' }}>
                  <strong>What's included in the PDF:</strong>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px',
                  maxWidth:'480px', margin:'0 auto 24px', textAlign:'left' }}>
                  {[
                    'Cover page','General Information','Table of Contents',
                    `${client?.entity_type==='CC'?"Members'":client?.entity_type==='Trust'?"Trustees'":"Directors'"} Responsibility Statement`,
                    `${client?.entity_type==='CC'?"Members'":client?.entity_type==='Trust'?"Trustees'":"Directors'"} Report`,
                    "Accountant's / Auditor's Report",
                    'Statement of Financial Position','Statement of Comprehensive Income',
                    'Statement of Changes in Equity','Statement of Cash Flows',
                    'Accounting Policies',`${totalNotes > 0 ? totalNotes : 22} Notes to the AFS`,
                  ].map((item, i) => (
                    <div key={i} style={{ fontSize:'12px', color:T.lt,
                      display:'flex', gap:'6px', alignItems:'center' }}>
                      <span style={{ color:T.gn }}><Icon name="check" /></span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                {/* iXBRL badge on pre-download screen */}
                <div style={{ display:'inline-flex', alignItems:'center', gap:'8px',
                  padding:'8px 14px', borderRadius:'8px',
                  background:T.bld, border:`1px solid ${alpha(T.bl, 0.3)}`,
                  marginBottom:'20px' }}>
                  <span style={{ fontSize:'14px' }}><Icon name="chart" /></span>
                  <span style={{ fontSize:'11px', color:T.bl, fontWeight:'600' }}>
                    iXBRL CIPC export also available after generation
                  </span>
                </div>

                {!first_year && Object.keys(prior_figures).length > 0 && (
                  <div style={{ fontSize:'12px', color:T.gn, marginBottom:'16px', fontWeight:'600' }}>
                    <Icon name="check" /> Prior year comparative figures included
                  </div>
                )}
                {totalNotes > 0 && approvedCount < totalNotes && (
                  <div style={{ background:alpha(T.am, 0.1), border:`1px solid ${alpha(T.am, 0.3)}`,
                    borderRadius:'8px', padding:'10px 16px', marginBottom:'16px',
                    fontSize:'12px', color:T.am }}>
                    <Icon name="alert" /> {totalNotes - approvedCount} note(s) not yet approved.
                  </div>
                )}
                <div style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
                  <button onClick={handlePreview}
                    disabled={previewLoading || showRPWarning || validationGateOpen}
                    title="Free — watermarked, view-only, no AFS credit used"
                    style={{ padding:'14px 28px', borderRadius:'10px',
                      border:`1.5px solid ${T.or}`, background:'transparent',
                      color:(previewLoading||showRPWarning||validationGateOpen)?T.mt:T.or,
                      fontFamily:"'Outfit',sans-serif", fontSize:'15px', fontWeight:'800',
                      cursor:(previewLoading||showRPWarning||validationGateOpen)?'not-allowed':'pointer' }}>
                    {previewLoading ? 'Loading preview…' : <><Icon name="eye" /> Preview AFS (free)</>}
                  </button>
                  <button onClick={handleGenerateFinal} disabled={loading || showRPWarning || validationGateOpen}
                    style={{ padding:'14px 32px', borderRadius:'10px', border:'none',
                      background:(loading||showRPWarning||validationGateOpen)?T.mt:T.prg,
                      color:T.onpri, fontFamily:"'Outfit',sans-serif", fontSize:'15px', fontWeight:'800',
                      cursor:(loading||showRPWarning||validationGateOpen)?'not-allowed':'pointer',
                      boxShadow:(showRPWarning||validationGateOpen)?'none':`0 4px 20px ${alpha(T.or, 0.35)}` }}>
                    {loading ? 'Generating...' : showRPWarning
                      ? `${openRPs} open review point${openRPs!==1?'s':''} — acknowledge above to proceed`
                      : validationBlocked
                        ? 'Resolve validation failure(s) above, or tick the override to proceed'
                        : validationWarned
                          ? 'Acknowledge the validation warning above to proceed'
                          : 'Generate Final AFS →'}
                  </button>
                </div>
                {!showRPWarning && !validationGateOpen && (
                  <div style={{ fontSize:'11px', color:T.mt, marginTop:'12px' }}>
                    Preview is free and unlimited — no AFS credit is used until you generate the final AFS
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
    </div>
  )
}

